import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorCode } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { FormFieldLabel } from '@/common/forms/FormInput';
import { InfoSectionContainer } from '@/common/InfoSection';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { TabsBar } from '@/common/TabsBar';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { CheckboxLabel, WBSCheckbox } from '@/common/WBSCheckbox';
import {
  BulkStudentConfirmModal,
  SKIP_REASON_KEYS,
} from '@/groups/coordinator/BulkStudentConfirmModal';
import { useAdminTabs } from '@/groups/coordinator/useAdminTabs';
import {
  noop,
  pickerLabel,
  useDebouncedSearch,
  useGroupStudentPicker,
} from '@/groups/coordinator/useGroupStudentPicker';
import { PageContent } from '@/settings/settingsStyles';
import {
  PEOPLE_PICKER_MIN_QUERY,
  PEOPLE_PICKER_PAGE_SIZE,
  type Person,
  useAsyncPeoplePicker,
} from '~/hooks/useAsyncPeoplePicker';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { SearchSelect } from '~/styles/forms/SearchSelect';
import { GroupHeaderWrapper } from '~/styles/layoutElements';
import { formatString } from '~/utils/stringFormat';

type SubstituteSelection = components['schemas']['GroupSubstituteSelection'];
type GroupSubstituteRequest = components['schemas']['GroupSubstituteRequest'];
type GroupSubstituteResult = components['schemas']['GroupSubstituteResult'];

/** `forbidden` is the reachable refusal; see the Release screen's note. */
const FAILURE_COPY = {
  forbidden: {
    title: 'groups.tag-permission-denied-title',
    body: 'wbs.you-dont-have-permission',
  },
} as const;

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/admin/substitute',
)({
  component: SubstituteStudentsScreenGate,
  // A junk `?substitute=` must degrade to the ordinary screen: NaN would engage
  // the deep-link branch and lock an empty picker. Only a positive id survives.
  validateSearch: (search): { substitute?: number } => {
    const id = Number(search.substitute);
    return { substitute: Number.isInteger(id) && id > 0 ? id : undefined };
  },
});

/**
 * The Settings layout admits the group's own study helper, so the screen
 * refuses everyone but platform admins before its queries and form exist.
 */
function SubstituteStudentsScreenGate(): React.ReactNode {
  const { t } = useTranslation();
  const { isAdmin, isPending } = useCurrentUser();
  if (isPending) return null;
  if (!isAdmin) {
    return (
      <PageContent>
        <AlertBox variant="error">{t('wbs.you-dont-have-permission')}</AlertBox>
      </PageContent>
    );
  }
  return <SubstituteStudentsForm />;
}

function SubstituteStudentsForm(): React.ReactNode {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const { substitute } = Route.useSearch();
  const navigate = useNavigate();
  const adminTabs = useAdminTabs();

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id ?? 0;

  const studentPicker = useGroupStudentPicker(groupId);

  // The target picker is site-wide with no adoptability check (legacy parity),
  // so it is the admin-gated study-helpers list, not the group-scoped one.
  const helperSearch = useDebouncedSearch();
  const { data: helperMatches, isFetching: helpersFetching } =
    api.getStudyHelpers.useQuery(
      {
        query: {
          search: helperSearch.search,
          limit: PEOPLE_PICKER_PAGE_SIZE,
        },
      },
      { enabled: helperSearch.search.length >= PEOPLE_PICKER_MIN_QUERY },
    );

  const helperResults = useMemo<Person[]>(
    () =>
      (helperMatches?.data ?? []).map((helper) => ({
        id: helper.id,
        name: pickerLabel(helper.display_name, helper.id),
      })),
    [helperMatches],
  );

  const helperPicker = useAsyncPeoplePicker(
    {
      results: helperResults,
      loading:
        helperSearch.term.length >= PEOPLE_PICKER_MIN_QUERY &&
        (helperSearch.term !== helperSearch.search || helpersFetching),
      onSearch: helperSearch.setTerm,
    },
    false,
  );

  const { data: counts } = api.getGroupStudentSelectionCounts.useQuery(
    { path: { group_id: groupId } },
    { enabled: groupId > 0 },
  );

  // `?substitute=<id>` preselects one student and locks the field. Resolved
  // through the group's own endpoint, so another group's student resolves to
  // nothing rather than leaking a name.
  const { data: deepLinked } = api.getGroupStudents.useQuery(
    { path: { group_id: groupId }, query: { student_id: substitute } },
    { enabled: substitute != null && groupId > 0 },
  );

  const { preselect: preselectStudent } = studentPicker;
  // Applied once per id, or the post-write refetch would put the just-moved
  // student back into the disabled field.
  const appliedDeepLink = useRef<number | null>(null);

  useEffect(() => {
    if (substitute == null || deepLinked === undefined) return;
    if (appliedDeepLink.current === substitute) return;
    appliedDeepLink.current = substitute;
    const row = deepLinked.data[0];
    preselectStudent(
      row
        ? { id: row.id, name: pickerLabel(row.display_name, row.id) }
        : // An unresolvable id renders as a deleted user, keeping the id suffix.
          {
            id: substitute,
            name: pickerLabel(t('actions.deleted-user'), substitute),
          },
    );
  }, [substitute, deepLinked, preselectStudent, t]);

  const [selection, setSelection] = useState<SubstituteSelection>('selected');
  const [confirming, setConfirming] = useState<{
    body: GroupSubstituteRequest;
    count: number;
  } | null>(null);
  const [result, setResult] = useState<GroupSubstituteResult | null>(null);
  const [selectionInvalid, setSelectionInvalid] = useState(false);
  const [helperInvalid, setHelperInvalid] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  // The last bulk request, kept so Run again can repost it while the server
  // reports `remaining` students (it processes a capped batch per request).
  const [lastBulk, setLastBulk] = useState<GroupSubstituteRequest | null>(null);

  const { mutateAsync: substituteStudents, isPending: isSubstituting } =
    api.substituteGroupStudents.useMutation();

  // A deep link makes the bulk modes unreachable: arriving from a single-student
  // link must not offer to move the whole group.
  const deepLink = substitute != null;
  const effectiveSelection: SubstituteSelection = deepLink
    ? 'selected'
    : selection;
  const studentsLocked = deepLink || effectiveSelection !== 'selected';

  // The checkboxes are mutually exclusive: server precedence is one enum.
  const toggleSelection = (mode: SubstituteSelection): void =>
    setSelection((current) => (current === mode ? 'selected' : mode));

  const handleSave = (): void => {
    setFailure(null);
    setResult(null);
    const studentIds = studentPicker.value.map(Number);
    const helperValue = helperPicker.value[0];
    // The target is validated before the selection.
    setHelperInvalid(helperValue === undefined);
    // A bulk mode that resolves to nothing is refused too; `counts` is only
    // consulted once resolved, and the count-0 banner is the backstop.
    const selectedCount =
      effectiveSelection === 'all'
        ? counts?.all_students
        : effectiveSelection === 'all_graduated'
          ? counts?.graduated_students
          : studentIds.length;
    setSelectionInvalid(selectedCount === 0);
    if (helperValue === undefined || selectedCount === 0) return;
    setConfirming({
      body: {
        selection: effectiveSelection,
        student_ids: effectiveSelection === 'selected' ? studentIds : [],
        study_helper_id: Number(helperValue),
      },
      count: selectedCount ?? 0,
    });
  };

  const handleConfirm = async (
    request: GroupSubstituteRequest | undefined = confirming?.body,
  ): Promise<void> => {
    if (request === undefined) return;
    setFailure(null);
    try {
      const written = await substituteStudents({
        path: { group_id: groupId },
        body: request,
      });
      setConfirming(null);
      setResult(written);
      setLastBulk(request.selection === 'selected' ? null : request);
      studentPicker.onChange([]);
      // Run again reposts the stored request, so the picker is not needed for it.
      helperPicker.onChange([]);
      setSelection('selected');
      await Promise.all([
        api.getGroupStudents.invalidateQueries(),
        api.getGroupStudentSelectionCounts.invalidateQueries(),
        // `students_adopted` reads the column this write moves.
        api.getGroupStudyHelpers.invalidateQueries(),
      ]);
    } catch (error) {
      setConfirming(null);
      setFailure(error);
    }
  };

  const failureCode = failure !== null ? errorCode(failure) : undefined;
  const failureCopy =
    failureCode !== undefined && failureCode in FAILURE_COPY
      ? FAILURE_COPY[failureCode as keyof typeof FAILURE_COPY]
      : undefined;

  // A 200 with `substituted: 0` means the resolved selection was empty.
  const substituted = result?.substituted ?? 0;
  const skipped = result?.skipped ?? [];
  const emptySelection =
    selectionInvalid ||
    (result !== null && substituted === 0 && skipped.length === 0);

  const skippedList = skipped.length > 0 && (
    <SkippedList>
      {skipped.map((student) => (
        <li key={student.student_id}>
          {student.display_name || t('actions.deleted-user')} —{' '}
          {t(SKIP_REASON_KEYS[student.reason])}
        </li>
      ))}
    </SkippedList>
  );

  return (
    <PageContent>
      <TabsBar tabs={adminTabs} base="/groups/$groupName/admin" />
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('wbs.substitute')} />
      </GroupHeaderWrapper>
      {helperInvalid && (
        <AlertBox variant="error">
          {t('lessons.please-select-study-helper')}
        </AlertBox>
      )}
      {/* Not `lessons.please-select-at-least`, whose English also names a study
          helper. */}
      {emptySelection && (
        <AlertBox variant="error">
          {t('groups.please-select-at-least-one-student')}
        </AlertBox>
      )}
      {failure !== null && (
        <AlertBox variant="error">
          <div>
            <strong>
              {failureCopy
                ? t(failureCopy.title)
                : t('wbs.something-went-wrong-please')}
            </strong>
            {failureCopy && <div>{t(failureCopy.body)}</div>}
          </div>
        </AlertBox>
      )}
      {substituted > 0 && (
        <AlertBox variant="success">
          <div>
            {/* The target helper's person name; the group name is an email token. */}
            <strong>
              {formatString(
                t('lessons.successfully-substituted-following-students'),
                result?.study_helper_name ?? '',
              )}
            </strong>
            {skippedList}
          </div>
        </AlertBox>
      )}
      {lastBulk !== null && (result?.remaining ?? 0) > 0 && (
        <AlertBox variant="info">
          <RemainingRow>
            <span data-testid="remaining-banner">
              {formatString(
                t('groups.d-students-remain'),
                String(result?.remaining ?? 0),
              )}
            </span>
            <WBSButton
              type="button"
              alternate
              isLoading={isSubstituting}
              onClick={() => {
                void handleConfirm(lastBulk);
              }}
            >
              {t('groups.run-again')}
            </WBSButton>
          </RemainingRow>
        </AlertBox>
      )}
      {/* Nothing applied, but something was named: the skip list is the whole
          explanation, so it renders without a success banner over it. */}
      {substituted === 0 && skipped.length > 0 && (
        <AlertBox variant="warning">
          <div>{skippedList}</div>
        </AlertBox>
      )}
      <InfoBox>
        <LargeArrowBackButton
          onClick={() =>
            navigate({
              to: '/groups/$groupName/admin/group-avatar/',
              params: { groupName },
            })
          }
        />
        {!deepLink && (
          <>
            <CheckRow>
              <CheckboxLabel>
                <WBSCheckbox
                  checked={selection === 'all'}
                  onChange={() => toggleSelection('all')}
                />
                {t('lessons.all-students')}
              </CheckboxLabel>
              <Counter data-testid="all-students-count">
                {counts?.all_students ?? 0}
              </Counter>
            </CheckRow>
            <CheckRow>
              <CheckboxLabel>
                <WBSCheckbox
                  checked={selection === 'all_graduated'}
                  onChange={() => toggleSelection('all_graduated')}
                />
                {t('groups.all-graduated-students')}
              </CheckboxLabel>
              <Counter data-testid="all-graduated-students-count">
                {counts?.graduated_students ?? 0}
              </Counter>
            </CheckRow>
          </>
        )}
        <Field>
          <FormFieldLabel>{t('lessons.students')}</FormFieldLabel>
          {/* Locked on a deep link; the id lives in state so the link still works. */}
          <SearchSelect
            multi
            disabled={studentsLocked}
            loading={studentPicker.loading}
            minQueryLength={PEOPLE_PICKER_MIN_QUERY}
            options={studentPicker.options}
            value={studentPicker.value}
            onChange={studentsLocked ? noop : studentPicker.onChange}
            onSearch={studentPicker.onSearch}
            placeholder={t('lessons.select-students')}
          />
        </Field>
        <Field>
          <FormFieldLabel>{t('lessons.study-helper')}</FormFieldLabel>
          <SearchSelect
            multi={false}
            loading={helperPicker.loading}
            minQueryLength={PEOPLE_PICKER_MIN_QUERY}
            options={helperPicker.options}
            value={helperPicker.value}
            onChange={helperPicker.onChange}
            onSearch={helperPicker.onSearch}
            placeholder={t('lessons.select-study-helper')}
          />
        </Field>
        <Footer>
          <WBSButton type="button" wide onClick={handleSave}>
            {t('wbs.confirm-substitution')}
          </WBSButton>
        </Footer>
      </InfoBox>
      {confirming !== null && (
        <BulkStudentConfirmModal
          title={t('wbs.substitute')}
          count={confirming.count}
          isConfirming={isSubstituting}
          onConfirm={() => {
            void handleConfirm();
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
    </PageContent>
  );
}

const InfoBox = styled(InfoSectionContainer)`
  position: relative;
`;

const CheckRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const Counter = styled.span`
  color: var(--grey-600);
  font-size: 13px;
`;

const Field = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const RemainingRow = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
`;

const SkippedList = styled.ul`
  margin: 8px 0 0 0;
  padding-left: 20px;
`;
